/**
 * Kontextmenü: Rechtsklick (Maus, Trackpad am iPad) auf einen Eintrag.
 *
 * Ein Menü für alle Listen, damit es überall gleich aussieht und sich gleich
 * verhält: öffnet an der Mausposition, schliesst bei Klick daneben, Escape,
 * Scrollen oder Auswahl. Gestaltet wie das Buch-Menü im Regal (shelf-menu),
 * kein zweites Designsystem.
 */
import { h } from './dom.ts';

export interface MenuItem {
  label: string;
  action: () => void;
  /** Rot, fuer Loeschen und Aehnliches. */
  danger?: boolean;
}

/** Trennlinie zwischen Gruppen. */
export const SEPARATOR = 'separator' as const;

export type MenuEntry = MenuItem | typeof SEPARATOR;

let openMenu: { el: HTMLElement; close: () => void } | null = null;

export function closeContextMenu(): void {
  openMenu?.close();
}

/** Zeigt ein Menü an der Stelle (x, y). */
export function showContextMenu(x: number, y: number, title: string, entries: MenuEntry[]): void {
  closeContextMenu();

  const el = h(
    'div',
    { class: 'shelf-menu context-menu', attrs: { role: 'menu' } },
    title ? h('p', { class: 'shelf-menu__title', text: title }) : null,
    ...entries.map((entry) =>
      entry === SEPARATOR
        ? h('div', { class: 'context-menu__sep', attrs: { role: 'separator' } })
        : h('button', {
            class: 'shelf-menu__item' + (entry.danger ? ' shelf-menu__item--danger' : ''),
            text: entry.label,
            attrs: { type: 'button', role: 'menuitem' },
            on: {
              click: () => {
                close();
                entry.action();
              },
            },
          }),
    ),
  );
  document.body.append(el);

  // Im Fenster halten, auch am rechten und unteren Rand.
  const rect = el.getBoundingClientRect();
  const left = Math.min(x, window.innerWidth - rect.width - 8);
  const top = Math.min(y, window.innerHeight - rect.height - 8);
  el.style.left = `${Math.max(8, left)}px`;
  el.style.top = `${Math.max(8, top)}px`;

  const onDown = (ev: PointerEvent) => {
    if (!el.contains(ev.target as Node)) close();
  };
  const onKey = (ev: KeyboardEvent) => {
    if (ev.key === 'Escape') close();
  };
  function close(): void {
    el.remove();
    document.removeEventListener('pointerdown', onDown, true);
    document.removeEventListener('keydown', onKey);
    window.removeEventListener('scroll', close, true);
    window.removeEventListener('hashchange', close);
    if (openMenu?.el === el) openMenu = null;
  }
  document.addEventListener('pointerdown', onDown, true);
  document.addEventListener('keydown', onKey);
  window.addEventListener('scroll', close, true);
  window.addEventListener('hashchange', close);
  openMenu = { el, close };
  (el.querySelector('button') as HTMLButtonElement | null)?.focus();
}

/**
 * Haengt ein Kontextmenü an ein Element. Die Einträge werden erst beim
 * Öffnen gebaut, damit sie den aktuellen Stand zeigen.
 */
export function withContextMenu<T extends HTMLElement>(el: T, title: string, entries: () => MenuEntry[]): T {
  el.addEventListener('contextmenu', (ev) => {
    ev.preventDefault();
    showContextMenu(ev.clientX, ev.clientY, title, entries());
  });
  return el;
}
